import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, filter, map, Observable, take } from 'rxjs';

export interface ModalRouteConfig {
  queryParam: string;
  open: (
    params?: Record<string, string>,
  ) => MatDialogRef<unknown> | Observable<MatDialogRef<unknown>>;
  escOnlyClose?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (result: any) => void;
}

@Injectable({ providedIn: 'root' })
export class RouteModalService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public initModalListeners(
    configs: ModalRouteConfig[],
    destroyRef: DestroyRef,
  ): void {
    this.route.queryParams
      .pipe(
        map((params) => {
          const cfg = configs.find((cfg) => params[cfg.queryParam] === 'true');
          return cfg ? { cfg, params } : null;
        }),
        distinctUntilChanged(
          (prev, curr) => prev?.cfg?.queryParam === curr?.cfg?.queryParam,
        ),
        filter((result) => !!result),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((result) => {
        const { cfg, params } = result;
        const dialogResult = cfg.open(params);
        if (dialogResult instanceof Observable) {
          dialogResult.pipe(take(1)).subscribe((dialogRef) => {
            this.handleAfterClose(dialogRef, cfg);
          });
        } else {
          this.handleAfterClose(dialogResult, cfg);
        }
      });
  }

  public openModal(queryParam: string, params?: Record<string, unknown>): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [queryParam]: 'true',
        ...params,
      },
      queryParamsHandling: 'merge',
    });
  }

  public removeQueryParam(queryParams: string | string[]): void {
    const keys = Array.isArray(queryParams) ? queryParams : [queryParams];
    const nullified = keys.reduce((acc, key) => ({ ...acc, [key]: null }), {});
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: nullified,
      queryParamsHandling: 'merge',
    });
  }

  private handleAfterClose(
    dialogRef: MatDialogRef<unknown>,
    cfg: ModalRouteConfig,
  ): void {
    if (cfg.escOnlyClose) {
      this.applyEscOnlyClosePolicy(dialogRef);
    }
    dialogRef.afterClosed().subscribe((result: unknown) => {
      if (cfg.onClose) {
        cfg.onClose(result);
      } else {
        this.removeQueryParam([cfg.queryParam, 'id', 'name', 'color']);
      }
    });
  }

  private applyEscOnlyClosePolicy(dialogRef: MatDialogRef<unknown>): void {
    dialogRef.disableClose = true;
    dialogRef
      .keydownEvents()
      .pipe(
        filter((event: KeyboardEvent) => event.key === 'Escape'),
        take(1),
      )
      .subscribe(() => {
        dialogRef.close();
      });
  }
}
